import {
  ApiBlueprint,
  AppRootElementBlueprint,
  configApiRef,
  createApiFactory,
  createFrontendModule,
  identityApiRef,
  oktaAuthApiRef,
} from '@backstage/frontend-plugin-api';
import {
  IconBundleBlueprint,
  RouterBlueprint,
  SignInPageBlueprint,
} from '@backstage/plugin-app-react';
import { createContext, useContext, useMemo, ReactNode } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { useApi } from '@backstage/frontend-plugin-api';
import { compatWrapper } from '@backstage/core-compat-api';
import { SignalsDisplay } from '@backstage/plugin-signals';
import { SignInPage } from '@backstage/core-components';
import {
  ScmAuth,
  ScmIntegrationsApi,
  scmIntegrationsApiRef,
} from '@backstage/integration-react';
import { costInsightsApiRef } from '@backstage-community/plugin-cost-insights';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSlack } from '@fortawesome/free-brands-svg-icons';
import { CostInsightsClient } from '../../api/CostInsightsClient';
import { sidebarContent } from '../nav';
import { legacyPageNavItems } from './navItems';

const signInPage = SignInPageBlueprint.make({
  params: {
    loader: async () => props =>
      process.env.NODE_ENV === 'development' ? (
        <SignInPage {...props} providers={['guest']} />
      ) : (
        <SignInPage
          {...props}
          providers={[
            {
              id: 'okta-auth-provider',
              title: 'Okta',
              message: 'Sign in using Okta',
              apiRef: oktaAuthApiRef,
            },
          ]}
        />
      ),
  },
});

const icons = IconBundleBlueprint.make({
  name: 'custom-icons',
  params: {
    icons: {
      slack: () => <FontAwesomeIcon icon={faSlack} />,
    },
  },
});

const scmIntegrationsApi = ApiBlueprint.make({
  name: 'scm-integrations',
  params: defineParams =>
    defineParams(
      createApiFactory({
        api: scmIntegrationsApiRef,
        deps: { configApi: configApiRef },
        factory: ({ configApi }) => ScmIntegrationsApi.fromConfig(configApi),
      }),
    ),
});

const scmAuthApi = ApiBlueprint.make({
  name: 'scm-auth',
  params: defineParams => defineParams(ScmAuth.createDefaultApiFactory()),
});

const costInsightsApi = ApiBlueprint.make({
  name: 'cost-insights',
  params: defineParams =>
    defineParams(
      createApiFactory({
        api: costInsightsApiRef,
        deps: { configApi: configApiRef, identityApi: identityApiRef },
        factory: ({ configApi, identityApi }) =>
          new CostInsightsClient({ configApi, identityApi }),
      }),
    ),
});

/**
 * Replaces the default plain `BrowserRouter` with a react-router data router
 * so that data-router-only APIs (notably `useBlocker`, used to guard unsaved
 * changes in secrets-configs) work throughout the app. The whole app renders
 * under a single splat route; descendant `<Routes>` (FlatRoutes, plugin
 * pages) keep working as before. The route element can't receive `children`
 * directly, so they're passed via context from outside the provider.
 */
const RouterChildrenContext = createContext<ReactNode>(null);
const RouterShell = () => <>{useContext(RouterChildrenContext)}</>;

const DataRouter = ({ children }: { children: ReactNode }) => {
  const configApi = useApi(configApiRef);
  const router = useMemo(() => {
    const baseUrl = configApi.getOptionalString('app.baseUrl');
    const basename = baseUrl
      ? new URL(baseUrl).pathname.replace(/\/$/, '')
      : '';
    return createBrowserRouter([{ path: '*', element: <RouterShell /> }], {
      basename,
    });
  }, [configApi]);
  return (
    <RouterChildrenContext.Provider value={children}>
      <RouterProvider router={router} />
    </RouterChildrenContext.Provider>
  );
};

const dataRouter = RouterBlueprint.make({
  params: {
    component: DataRouter,
  },
});

const signalsDisplay = AppRootElementBlueprint.make({
  name: 'signals-display',
  params: {
    element: compatWrapper(<SignalsDisplay />),
  },
});

/**
 * App-level customizations: sidebar, sign-in page, icons and API
 * implementations that glue third-party plugins to our backends.
 */
export const appModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    sidebarContent,
    dataRouter,
    signInPage,
    signalsDisplay,
    icons,
    scmIntegrationsApi,
    scmAuthApi,
    costInsightsApi,
    ...legacyPageNavItems,
  ],
});
